import JSZip from 'jszip';
import { supabase } from '@/lib/supabase';

const PAGE_SIZE = 1000;

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyQuery = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

async function fetchAll<T = Record<string, unknown>>(make: () => AnyQuery): Promise<T[]> {
  const rows: T[] = [];
  let from = 0;
  // Recorre todas las páginas hasta agotar los registros.
  for (let guard = 0; guard < 200; guard += 1) {
    const { data, error } = await make().range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return rows;
}

const PROFILE_HIDDEN = ['access_code_hash', 'access_code_salt'];

function sanitizeProfiles(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((row) => {
    const clean = { ...row };
    for (const key of PROFILE_HIDDEN) delete clean[key];
    return clean;
  });
}

export interface BackupScope {
  branchId: string | null;
  branchName?: string | null;
}

export interface BackupResult {
  data: Record<string, unknown[]>;
  fileName: string;
  meta: {
    type: string;
    branchId: string | null;
    branchName: string | null;
    createdAt: string;
    tables: { name: string; rows: number }[];
  };
}

export async function buildBackupData(scope: BackupScope): Promise<BackupResult> {
  const { branchId } = scope;
  const data: Record<string, unknown[]> = {};

  const add = async (name: string, make: () => AnyQuery) => {
    data[name] = await fetchAll(make);
  };

  // Sucursales
  await add('branches', () => {
    let q = supabase.from('branches').select('*').order('created_at');
    if (branchId) q = q.eq('id', branchId);
    return q;
  });

  // Catálogo (global / compartido entre sucursales)
  await add('categories', () => supabase.from('categories').select('*').order('name'));
  await add('products', () => supabase.from('products').select('*').order('name'));
  await add('clients', () => supabase.from('clients').select('*').order('created_at'));

  // Configuración
  await add('sales_settings', () => supabase.from('sales_settings').select('*'));
  await add('business_info', () => supabase.from('business_info').select('*'));
  await add('settings_audit_log', () =>
    supabase.from('settings_audit_log').select('*').order('created_at')
  );

  // Usuarios / cajeros
  const profileRows = await fetchAll(() => {
    let q = supabase.from('profiles').select('*').order('created_at');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  });
  data.profiles = sanitizeProfiles(profileRows as Record<string, unknown>[]);

  // Inventario por sucursal
  await add('branch_inventory', () => {
    let q = supabase.from('branch_inventory').select('*').order('created_at');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  });

  await add('inventory_movements', () => {
    let q = supabase.from('inventory_movements').select('*').order('created_at');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  });

  // Ventas y sus detalles
  const sales = await fetchAll<{ id: string }>(() => {
    let q = supabase.from('sales').select('*').order('created_at');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  });
  data.sales = sales;

  const saleIds = sales.map((s) => s.id);
  if (saleIds.length > 0) {
    const items: unknown[] = [];
    for (let i = 0; i < saleIds.length; i += 100) {
      const chunk = saleIds.slice(i, i + 100);
      const rows = await fetchAll(() => supabase.from('sale_items').select('*').in('sale_id', chunk));
      items.push(...rows);
    }
    data.sale_items = items;
  } else {
    data.sale_items = [];
  }

  // Auditoría / historial
  await add('audit_log', () => {
    let q = supabase.from('audit_log').select('*').order('created_at');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  });
  await add('import_history', () => {
    let q = supabase.from('import_history').select('*').order('created_at');
    if (branchId) q = q.eq('branch_id', branchId);
    return q;
  });

  // Dispositivos / login (solo en backup completo)
  if (!branchId) {
    await add('devices', () => supabase.from('devices').select('*').order('created_at'));
    await add('login_history', () => supabase.from('login_history').select('*').order('created_at'));
  }

  const createdAt = new Date().toISOString();
  const date = createdAt.slice(0, 10);
  const typeLabel = branchId ? 'sucursal' : 'completo';
  const fileName = branchId
    ? `backup_sucursal_${date}.zip`
    : `backup_sistema_${date}.zip`;

  return {
    data,
    fileName,
    meta: {
      type: typeLabel,
      branchId,
      branchName: scope.branchName ?? null,
      createdAt,
      tables: Object.entries(data).map(([name, rows]) => ({ name, rows: rows.length })),
    },
  };
}

async function sha256Hex(text: string): Promise<string> {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
      return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch {
    /* navegador sin Web Crypto */
  }
  return 'no-disponible';
}

export async function downloadBackupZip(result: BackupResult): Promise<number> {
  const zip = new JSZip();
  const files: { name: string; rows: number; bytes: number; sha256: string }[] = [];

  for (const [name, rows] of Object.entries(result.data)) {
    const content = JSON.stringify(rows, null, 2);
    zip.file(`${name}.json`, content);
    files.push({
      name: `${name}.json`,
      rows: rows.length,
      bytes: content.length,
      sha256: await sha256Hex(content),
    });
  }

  const manifest = {
    ...result.meta,
    generatedBy: 'DK Supply POS',
    files,
  };

  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  zip.file(
    'LEEME.txt',
    [
      'Copia de seguridad — Sistema de Facturación',
      `Tipo: ${result.meta.type}`,
      `Sucursal: ${result.meta.branchName ?? 'Todas'}`,
      `Generado: ${result.meta.createdAt}`,
      '',
      'Cada archivo .json contiene una tabla de la base de datos.',
      'El archivo manifest.json incluye la huella SHA-256 de cada archivo.',
      'Para comprobar que la copia no está dañada, calcula el SHA-256 del .json',
      'y compáralo con el valor del manifiesto: si coinciden, la copia es íntegra.',
      '',
      'IMPORTANTE: guarda este archivo en un lugar seguro y fuera del sitio web.',
      'Esta copia contiene datos de clientes y ventas; no la compartas públicamente.',
    ].join('\n')
  );

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = result.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return blob.size;
}